<template>
  <div
    class="c-collapseSubCategories"
    :style="{ 'max-height': `${height}px` }"
    :class="{ 'c-collapseSubCategories-opened': isOpened }"
  >
    <ul
      ref="content"
      class="c-collapseSubCategories_list"
    >
      <li class="c-collapseSubCategories_listItem">
        <component
          class="c-collapseSubCategories_listItemLink"
          :is="isFromSearchService ? 'a' : 'nuxt-link'"
          v-bind="parentCategoryRouteAttrs"
        >
          すべて
          <coconala-icon
            name="chevron-right"
            size="12px"
            color="ds2Gray600"
          />
        </component>
      </li>
      <li
        v-if="isFortuneCategory"
        class="c-collapseSubCategories_listItem"
      >
        <component
          class="c-collapseSubCategories_listItemLink"
          :is="isFromSearchService ? 'a' : 'nuxt-link'"
          v-bind="phoneConsultationServiceListRouteAttrs"
        >
          <span>
            電話占い
          </span>
          <coconala-icon
            name="chevron-right"
            size="12px"
            color="ds2Gray600"
          />
        </component>
      </li>
      <li
        v-for="subCategory in category.subCategories"
        :key="subCategory.id"
        class="c-collapseSubCategories_listItem"
      >
        <component
          class="c-collapseSubCategories_listItemLink"
          :is="isFromSearchService ? 'a' : 'nuxt-link'"
          v-bind="getCategoryRouteAttrs(subCategory)"
        >
          {{ subCategory.name }}
          <DNewLabel
            v-if="subCategory.isNewLabel"
            class="c-collapseSubCategories_newLabel"
          />
          <coconala-icon
            name="chevron-right"
            size="12px"
            color="ds2Gray600"
          />
        </component>
      </li>
      <!-- 関連カテゴリのリンクを表示 -->
      <li
        v-for="relatedCategory in relatedCategories"
        :key="`relatedCategory${relatedCategory.id}`"
        class="c-collapseSubCategories_listItem"
      >
        <component
          class="c-collapseSubCategories_listItemLink"
          :is="isFromSearchService ? 'a' : 'nuxt-link'"
          v-bind="getCategoryRouteAttrs(relatedCategory)"
        >
          {{ relatedCategory.name }}
          <coconala-icon
            name="chevron-right"
            size="12px"
            color="ds2Gray600"
          />
        </component>
      </li>
      <!-- 士業は法律相談へのリンクを表示 -->
      <li
        v-if="category.id === 13"
        class="c-collapseSubCategories_listItem"
      >
        <a
          class="c-collapseSubCategories_listItemLink"
          :href="legalUrl"
          target="_blank"
          rel="noopener"
        >
          法律相談・弁護士検索
          <coconala-icon
            class="c-collapseSubCategories_listItemIcon"
            name="external-link"
            size="12px"
            color="ds2Gray600"
          />
        </a>
      </li>
    </ul>
  </div>
</template>

<script>
import SubCategoriesMixin from '@/mixins/category/sub-categories-mixin'
import qs from 'qs'

// vuex
import { mapGetters } from 'vuex'

const DEFERRED_QUERIES = Object.freeze(['sort_by', 'exclude_full_correspondence', 'keyword'])

/** @type {import('vue').ComponentOptions} */
const collapseSubCategories = {
  name: 'CollapseSubCategories',
  components: {
    DNewLabel: () => import('~/components/atoms/DS2/DNewLabel')
  },
  mixins: [SubCategoriesMixin],
  props: {
    category: {
      type: Object,
      required: true
    },
    parentCategoryId: {
      type: Number,
      default: 0
    },
    isProCert: {
      type: Boolean,
      default: false
    },
    isOpened: {
      type: Boolean,
      default: false
    },
    isFromSearchService: {
      type: Boolean,
      default: false
    },
    legalUrl: {
      type: String,
      required: true
    },
    isBusinessBeforeLogin: {
      type: Boolean,
      default: false
    },
    deferSearchQueries: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      maxHeight: 0
    }
  },
  computed: {
    ...mapGetters('constants', ['getRelatedCategories']),
    height() {
      return this.isOpened ? this.maxHeight : 0
    },
    relatedCategories() {
      return this.getRelatedCategories(this.parentCategoryId)
    },
    parentCategoryRouteAttrs() {
      const queryFilter = key => DEFERRED_QUERIES.includes(key)

      if (this.isFromSearchService) {
        let href = this.parentCategoryRoute(false, true, this.isBusinessBeforeLogin)
        if (this.deferSearchQueries) {
          href = this.getDeferredQueryPath(href, queryFilter)
        }
        return { href }
      }
      let to = this.parentCategoryRoute(true, false, this.isBusinessBeforeLogin)
      if (this.deferSearchQueries) {
        to = this.getDeferredQueryRoute(to, queryFilter)
      }
      return { to }
    },
    phoneConsultationServiceListRouteAttrs() {
      const queryFilter = key => DEFERRED_QUERIES.includes(key)

      if (this.isFromSearchService) {
        let href = this.phoneConsultationServiceListRoute(false, true, this.isBusinessBeforeLogin)
        if (this.deferSearchQueries) {
          href = this.getDeferredQueryPath(href, queryFilter)
        }
        return { href }
      }
      let to = this.phoneConsultationServiceListRoute(true, false, this.isBusinessBeforeLogin)
      if (this.deferSearchQueries) {
        to = this.getDeferredQueryRoute(to, queryFilter)
      }
      return { to }
    }
  },
  methods: {
    /**
     * 現在ページのクエリパラメータを引き継いだ遷移先パスを取得
     * @param {string} fullPath 遷移先フルパス
     * @param {(key: string, value?: string|object) => boolean} filter 引き継ぐクエリパラメータを絞るためのフィルタ
     */
    getDeferredQueryPath(fullPath, filter = null) {
      const [basePath, baseQuery] = fullPath.split('?')
      const query = Object.assign(
        qs.parse(baseQuery ?? ''),
        Object.fromEntries(
          Object.entries(this.$route.query).filter(([key, value]) => !filter || filter(key, value))
        )
      )

      const path = `${basePath}?${qs.stringify(query)}`
      if (this.hasKeywordQueryString(path)) {
        return this.convertToSearchPath(path)
      }
      return path
    },
    /**
     * 現在ページのクエリパラメータを引き継いだ遷移先ルートを取得
     * @param {import('vue-router').Route} route 遷移先ルート
     * @param {(key: string, value: string|object) => boolean} filter 引き継ぐクエリパラメータを絞るためのフィルタ
     */
    getDeferredQueryRoute(route, filter = null) {
      const query = Object.assign(
        {},
        route.query,
        Object.fromEntries(
          Object.entries(this.$route.query).filter(([key, value]) => !filter || filter(key, value))
        )
      )

      const deferredQueryRoute = {
        ...route,
        query
      }
      if (this.hasKeywordQuery(deferredQueryRoute)) {
        return this.convertToSearchRoute(deferredQueryRoute)
      }
      return deferredQueryRoute
    },
    /**
     * 対象カテゴリページへのパス/ルートを取得
     * @param {import('~/stub/apigateway/master/v2/master_pb').Category.AsObject} category
     */
    getCategoryRouteAttrs(category) {
      const queryFilter = key => DEFERRED_QUERIES.includes(key)

      if (this.isFromSearchService) {
        let href = this.categoryRoute(category, false, true, this.isBusinessBeforeLogin)
        if (this.deferSearchQueries) {
          href = this.getDeferredQueryPath(href, queryFilter)
        }
        return { href }
      }
      let to = this.categoryRoute(category, true, false, this.isBusinessBeforeLogin)
      if (this.deferSearchQueries) {
        to = this.getDeferredQueryRoute(to, queryFilter)
      }
      return { to }
    },
    /**
     * カテゴリページのパス(/categories/:categoryId)を検索TOPのパス(/search?category_id=:categoryId)に変換
     * @param {string} path
     * @returns {string}
     */
    convertToSearchPath(path) {
      return path
        .replace(/^\/categories\/(\d+)(?:\?(.+))?/, '/search?category_id=$1&$2')
        .replace(/[&?]*$/, '')
    },
    /**
     * カテゴリページのルートを検索TOPのルートに変換
     * @param {import('vue-router').Route} route
     * @returns {import('vue-router').Route}
     */
    convertToSearchRoute(route) {
      const {
        query,
        params: { categoryId }
      } = route
      return {
        name: 'search',
        params: {},
        query: Object.assign({}, { category_id: categoryId }, query)
      }
    },
    /**
     * @param {string} path
     */
    hasKeywordQueryString(path) {
      const [, queries] = path.split('?')
      const regExp = /^keyword=/
      return !!queries && queries.split('&').some(v => regExp.test(v))
    },
    /**
     * @param {import('vue-router').Route} pathOrRoute
     */
    hasKeywordQuery(route) {
      return Object.keys(route.query).includes('keyword')
    }
  },
  mounted() {
    const contentRect = this.$refs.content?.getBoundingClientRect()
    if (!contentRect) {
      return
    }
    this.maxHeight = contentRect.height
  }
}
export default collapseSubCategories
</script>

<style lang="scss" scoped>
.c-collapseSubCategories {
  overflow-y: hidden;
  opacity: 0;
  transition: all 0.3s ease-out;
  &-opened {
    opacity: 1;
  }
  &_list {
    display: flex;
    flex-direction: column;
  }
  &_listItem {
    $item-margin-left: 46px;

    margin-left: $item-margin-left;
    padding: 12px 0;
    width: calc(100% - #{$item-margin-left});

    & + & {
      border-top: 1px solid $ds2-color-gray-25;
    }
  }
  &_newLabel {
    margin-bottom: 3px;
    margin-left: 8px;
  }
  &_listItemLink {
    @include link-effect;

    display: flex;
    padding: 0 12px 0 14px;
    width: 100%;
    color: $ds2-color-gray-900;
    font-size: 14px;
    line-height: normal;

    align-items: center;
    /deep/ .-chevron-right {
      margin-left: auto;
    }
  }
  &_listItemIcon {
    margin-left: 4px;
  }
}
</style>
